import * as React from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { ChatMessageComponents } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatMessageComponents.tsx";
import { ThinkingIndicator } from "@/shared/components";
import { ChatMessageStatus, ChatRecord } from "@/shared/domain/chat";
import {
  ChatStatus,
  isChatToolUseContent,
} from "@/shared/domain/chat/ChatRecord.ts";

import styles from "./ChatByIdView.module.css";

interface ChatByIdViewProps {
  chat: ChatRecord;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

export const ChatByIdView = ({ chat, messagesEndRef }: ChatByIdViewProps) => {
  const { handleLinkClick, autoNavEnabled } = useAIChat();
  const thinking = React.useMemo(() => {
    if (chat.messages.length === 0) {
      return true;
    }

    if (chat.status === ChatStatus.USER_TURN) {
      return false;
    }

    const lastMessage = chat.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isChatToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === ChatMessageStatus.COMPLETED;
  }, [chat]);

  return (
    <>
      <div className={styles.centeringContainer}>
        <div
          className={styles.contentContainer}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          {chat.messages.length > 0 && (
            <>
              {chat.messages.map((message, index) => (
                <ChatMessageComponents
                  key={index}
                  message={message}
                  isLastMessageInChat={index === chat.messages.length - 1}
                  onLinkClick={handleLinkClick}
                  autoNavDedupeKey={`${chat.chat_id}-${index}`}
                  autoNavEnabled={autoNavEnabled}
                />
              ))}
            </>
          )}
          {/* Invisible element for auto-scroll */}
          {thinking && <ThinkingIndicator />}
        </div>
      </div>
      <div ref={messagesEndRef} />
    </>
  );
};
